import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Bar, BarChart, XAxis, YAxis } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { usdFormatter } from "./usd-tooltip-formatter"
import { formatUSD } from "../../utils/format-helpers"

type SavingsSplitChartProps = {
  useCases: DashboardUseCase[]
}

export function SavingsSplitChart({ useCases }: SavingsSplitChartProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        softSavings: {
          label: t("useCases.dashboard.charts.softSavings"),
          color: "hsl(var(--chart-1))",
        },
        hardSavings: {
          label: t("useCases.dashboard.charts.hardSavings"),
          color: "hsl(var(--chart-2))",
        },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo(() => {
    return [...useCases]
      .sort((a, b) => b.annualBenefit - a.annualBenefit)
      .slice(0, 15)
      .map((uc) => ({
        name: uc.ucName.length > 25 ? uc.ucName.slice(0, 22) + "..." : uc.ucName,
        fullName: uc.ucName,
        softSavings: uc.softSavings,
        hardSavings: uc.hardSavings,
      }))
  }, [useCases])

  if (data.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.savingsSplit")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer
          className="w-full"
          config={chartConfig}
          style={{ height: Math.max(200, data.length * 32) }}
        >
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
          >
            <XAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatUSD(v)} type="number" />
            <YAxis dataKey="name" tick={{ fontSize: 11 }} type="category" width={140} />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={usdFormatter}
              labelFormatter={(_label, payload) => payload[0]?.payload?.fullName ?? _label}
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              dataKey="softSavings"
              fill="hsl(var(--chart-1))"
              name={t("useCases.dashboard.charts.softSavings")}
              radius={[0, 0, 0, 0]}
              stackId="stack"
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
            />
            <Bar
              dataKey="hardSavings"
              fill="hsl(var(--chart-2))"
              name={t("useCases.dashboard.charts.hardSavings")}
              radius={[0, 4, 4, 0]}
              stackId="stack"
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
